import { Box, Text } from '@chakra-ui/react';

import {
  DynamicPriceDescription,
  DynamicPriceTitle,
} from '~/features/accreditation/components/dynamic-price-top-bar-item';
import { WoolfProgram } from '~/features/accreditation/components/woolf-program-card';
import electiveKeys from '~/features/accreditation/constants/approved-elective-keys.json';
import { mastersAiNewsItems } from '~/features/accreditation/constants/news-items';
import { AccreditationPageContentProps } from '~/features/accreditation/types';
import {
  BookIcon,
  CircleArrowRightIcon,
  CircleCheckIcon,
  ClockIcon,
  DegreeIcon,
  RocketIcon,
  SchoolIcon,
  TagIcon,
} from '~/features/experiments/ab_plp_redesign_2025/icons';

const ICON_SIZE = { base: '18px', md: '24px' };
const TOPBAR_ICON_SIZE = { base: '18px', lg: '24px' };

/** Maps Woolf milestone `program.key` to consumer nanodegree `nodeKey`s. Omits `masters-ai-ethical-ai` — ethics is Masters-only, not a sold program. */
const mastersAiWoolfToUdacityProgramKeys: Record<string, string[]> = {
  'woolf-intro-artificial-intelligence': ['nd608'],
  'woolf-intelligent-systems': ['nd900'],
  'woolf-intro-machine-learning': ['nd229'],
  'woolf-advanced-artificial-intelligence-concepts': ['nd898'],
  'woolf-intro-deep-learning': ['nd101'],
  'woolf-intro-computer-programming-part-1': ['nd089'],
  'woolf-applied-data-analytics': ['nd002', 'nd588'],
};

const mastersAiCorePrograms: WoolfProgram[] = [
  {
    key: 'woolf-intro-artificial-intelligence',
    title: 'Introduction to Artificial Intelligence',
    image: null,
    semanticType: 'Woolf_Program',
    rawDurationDisplay: '125 hours',
  },
  {
    key: 'woolf-intelligent-systems',
    title: 'Intelligent Systems',
    image: null,
    semanticType: 'Woolf_Program',
    rawDurationDisplay: '125 hours',
  },
  {
    key: 'woolf-intro-machine-learning',
    title: 'Introduction to Machine Learning',
    image: null,
    semanticType: 'Woolf_Program',
    rawDurationDisplay: '125 hours',
  },
  {
    key: 'woolf-advanced-artificial-intelligence-concepts',
    title: 'Advanced Artificial Intelligence Concepts',
    image: null,
    semanticType: 'Woolf_Program',
    rawDurationDisplay: '125 hours',
  },
  {
    key: 'woolf-intro-deep-learning',
    title: 'Introduction to Deep Learning',
    image: null,
    semanticType: 'Woolf_Program',
    rawDurationDisplay: '125 hours',
  },
  {
    key: 'woolf-intro-computer-programming-part-1',
    title: 'Introduction to Computer Programming: Part 1',
    image: null,
    semanticType: 'Woolf_Program',
    rawDurationDisplay: '125 hours',
  },
  {
    key: 'woolf-applied-data-analytics',
    title: 'Applied Data Analytics',
    image: null,
    semanticType: 'Woolf_Program',
    rawDurationDisplay: '125 hours',
  },
  {
    // Internal Masters in AI module only — not mirrored as a consumer nanodegree in `mastersAiWoolfToUdacityProgramKeys`.
    key: 'masters-ai-ethical-ai',
    title: 'Ethical Artificial Intelligence Practices',
    image: 'https://cdn.sanity.io/images/tlr8oxjg/production/9deb5ea5e8c73416faf3e8cf9300eb0bc7e60a8f-480x240.png',
    semanticType: 'Woolf_Program',
    rawDurationDisplay: '125 hours',
  },
];

export const mastersAiContent: AccreditationPageContentProps = {
  colorScheme: 'blue',
  formalDegreeKey: 'ms_ai',

  hero: {
    title: "Advance your career with a Master's in AI",
    description:
      'Earn an accredited AI degree\u2014built on real projects, flexible to your schedule and designed with your career goals in mind.',
    brochureUri: 'https://cdn.sanity.io/files/tlr8oxjg/production/f054918c0c1f8f5bc2b69164418d1069a3a501c7.pdf',
    backgroundImages: {
      mobile: '/images/accreditation/hero-bg-mobile.png',
      desktop: '/images/accreditation/hero-bg.png',
      objectPosition: {
        mobile: 'top right',
        desktop: 'bottom',
      },
    },
  },

  topBarItems: [
    {
      id: 'price',
      icon: <TagIcon color='gold.300' h={TOPBAR_ICON_SIZE} w={TOPBAR_ICON_SIZE} />,
      title: <DynamicPriceTitle fallback='Less than $5,000 USD*' formalDegreeKey='ms_ai' />,
      description: (
        <DynamicPriceDescription fallback='Only $199 + Udacity subscription to start.' formalDegreeKey='ms_ai' />
      ),
    },
    {
      id: 'duration',
      icon: <ClockIcon color='gold.300' h={TOPBAR_ICON_SIZE} w={TOPBAR_ICON_SIZE} />,
      title: '18-24 months',
      description: 'Avg time to complete 2,250 study hours',
    },
    {
      id: 'capstone',
      icon: <BookIcon color='gold.300' h={TOPBAR_ICON_SIZE} w={TOPBAR_ICON_SIZE} />,
      title: 'Capstone project',
      description: 'Graduate with a degree and portfolio',
    },
    {
      id: 'degree',
      icon: <DegreeIcon color='gold.300' h={TOPBAR_ICON_SIZE} w={TOPBAR_ICON_SIZE} />,
      title: "Master's in AI",
      description: 'Accredited degree awarded by Woolf',
    },
  ],

  values: {
    sectionTitle: "A Master's built around you",
    sectionDescription: (
      <Text color='grey.500' margin='0 auto' maxWidth='650px'>
        <Box as='span' color='grey.900' fontWeight='700'>
          The Udacity Institute of AI & Technology
        </Box>{' '}
        combines real-world projects with expert support, so you don&apos;t just learn, you advance.
      </Text>
    ),
    items: [
      {
        imageSrc: '/images/accreditation/value-icon--chart-2x.png',
        title: 'Modular journey to AI mastery',
        description:
          'Learn step by step, from foundational AI concepts to advanced applications, on a customizable path built for you.',
      },
      {
        imageSrc: '/images/accreditation/value-icon--compass-2x.png',
        title: 'Guidance from Mentors',
        description:
          'Get support from experienced mentors who help you stay on track, answer questions, and guide your progress',
      },
      {
        imageSrc: '/images/accreditation/value-icon--circle-2x.png',
        title: 'Flexible and 100% online',
        description:
          'Study anytime, at your convenience. Balance your degree with work and life without compromising outcomes.',
      },
      {
        imageSrc: '/images/accreditation/value-icon--rod-2x.png',
        title: 'Project-based learning',
        description:
          'Build a portfolio of real-world projects that showcase your skills and set you apart in the job market.',
      },
    ],
  },

  stats: {
    heading: 'Maximum career impact, lasting value',
    subheading:
      "A recognized Master's Degree in AI that delivers both a respected credential and meaningful career advancement.",
    stats: [
      { number: '56', descriptionTop: 'Higher wages', descriptionBottom: 'for workers with AI skills', suffix: 1 },
      {
        number: '38',
        descriptionTop: 'Growth in AI related jobs',
        descriptionBottom: 'globally over the last 5 years',
        suffix: 1,
      },
      {
        number: '89',
        descriptionTop: 'More affordable',
        descriptionBottom: 'compared to a $44k avg program',
        suffix: 2,
      },
    ],
    sources: [
      {
        suffix: 1,
        label: 'PwC 2025 Global AI Jobs Barometer',
        href: 'https://www.pwc.com/gx/en/services/ai/ai-jobs-barometer.html',
      },
      {
        suffix: 2,
        label: "The Most Affordable Online Master's in Artificial Intelligence Programs",
        href: 'https://techguide.org/computer-science/most-affordable-online-masters-artificial-intelligence',
      },
    ],
  },

  pathTitle: "Starting your Master's journey",
  pathDescription:
    'From your first step through your graduation, the process is simple, flexible, and designed to deliver results.',
  pathSteps: [
    {
      icon: <RocketIcon color='gold.500' h={ICON_SIZE} w={ICON_SIZE} />,
      title: '1. Subscribe to Udacity',
      description: 'Become a subscriber to unlock the application process.',
    },
    {
      icon: <TagIcon color='gold.500' h={ICON_SIZE} w={ICON_SIZE} />,
      title: '2. Pay enrollment fee',
      description: 'All applicants must pay a one-time non-refundable fee.',
    },
    {
      icon: <CircleCheckIcon color='gold.500' h={ICON_SIZE} w={ICON_SIZE} />,
      title: '3. Apply online',
      description: 'Submit a short application.',
    },
    {
      icon: <CircleArrowRightIcon color='gold.500' h={ICON_SIZE} w={ICON_SIZE} />,
      title: '4. Start learning',
      description: 'Access the full curriculum, projects, and mentorship.',
    },
    {
      icon: <SchoolIcon color='gold.500' h={ICON_SIZE} w={ICON_SIZE} />,
      title: '5. Earn credit',
      description: 'Earn credit for prior and current learning.',
    },
    {
      icon: <DegreeIcon color='gold.500' h={ICON_SIZE} w={ICON_SIZE} />,
      title: "6. Receive your Master's in AI",
      description: 'Graduate and go forth with confidence in your capabilities!',
    },
  ],

  about: {
    certificateImage: '/images/accreditation/udacity-woolf-degree.png',
    description: (
      <Text color='grey.500'>
        The Udacity Institute of AI and Technology, a member college of Woolf, offers a pathway to a Master of Science
        in Artificial Intelligence. Students complete Udacity&apos;s hands-on, project-based curriculum while earning
        ECTS credits transferable across 50+ countries. Degrees are awarded by{' '}
        <Box as='a' color='blue.500' href='https://woolf.university' target='_blank' textDecoration='underline'>
          Woolf
        </Box>
        , a global collegiate Higher Education Institution, ensuring the same academic recognition as other higher
        education institutions, at a fraction of the cost and considerably more flexibility than a traditional
        Master&apos;s program.
      </Text>
    ),
  },

  prerequisites: {
    degreeTitle: "Master's Degree in AI",
    feeDescription: 'One-time enrollment fee',
    prerequisites: [
      "Bachelor's degree (recommended) or equivalent experience.",
      'Foundational programming knowledge (Python recommended).',
      'Basic math & statistics background.',
      'English proficiency (for coursework and assessments).',
      'Udacity monthly subscription (required for application).',
      'Must be at least 16 years old at enrollment.',
    ],
    stampImage: '/images/accreditation/stamp.png',
  },

  milestones: {
    sectionTitle: "How you earn your Master's in AI",
    electiveKeys,
    electiveCreditHours: 625,
    capstoneCreditHours: 750,
    electiveDescription:
      'Choose from a large selection of electives and complete 625 unique credit hours in the areas that interest you most.',
    coreDescription: 'Build your foundation with these required core modules totaling 875 unique credit hours.',
    capstoneDescription:
      'Complete a seven-project, industry-style AI capstone where you design, build, and justify real AI systems - from ML models to generative and agentic AI. Graduate with a portfolio and interview-ready stories that prove your mastery.',
    modalAlertText: "The Udacity programs below need to be completed for this Master's course.",
    corePrograms: mastersAiCorePrograms,
    woolfToUdacityProgramKeys: mastersAiWoolfToUdacityProgramKeys,
  },

  testimonials: [
    {
      imagePath: '/images/experiments/ab_plp_redesign_2025/federico-martini.png',
      labelKey: 'testimonials.federicoMartini.label',
      quoteKey: 'testimonials.federicoMartini.quote',
      name: 'Federico Martini',
      title: 'AI Engineering Manager & Tech Leader',
      quoteColor: 'light',
      labelColor: 'dark',
    },
    {
      imagePath: '/images/experiments/ab_plp_redesign_2025/sabrina-palis.png',
      labelKey: 'testimonials.sabrinaPalis.label',
      quoteKey: 'testimonials.sabrinaPalis.quote',
      name: 'Sabrina Palis',
      title: 'AI & Data Consultant',
      quoteColor: 'light',
      labelColor: 'dark',
    },
    {
      imagePath: '/images/experiments/ab_plp_redesign_2025/allan-bravos.png',
      labelKey: 'testimonials.allanDiegoBravos.label',
      quoteKey: 'testimonials.allanDiegoBravos.quote',
      name: 'Allan Diego Bravos',
      title: 'Head of Data & AI',
      quoteColor: 'light',
      labelColor: 'light',
    },
    {
      imagePath: '/images/experiments/ab_plp_redesign_2025/jordan-shephard.png',
      labelKey: 'testimonials.jordanShephard.label',
      quoteKey: 'testimonials.jordanShephard.quote',
      name: 'Jordan Shephard',
      title: 'Product Manager',
      quoteColor: 'dark',
      labelColor: 'dark',
    },
    {
      imagePath: '/images/experiments/ab_plp_redesign_2025/rey-rosales.png',
      labelKey: 'testimonials.reyRosales.label',
      quoteKey: 'testimonials.reyRosales.quote',
      name: 'Rey Rosales',
      title: 'Professor',
      quoteColor: 'light',
      labelColor: 'light',
    },
    {
      imagePath: '/images/experiments/ab_plp_redesign_2025/subramanya-nataraj.png',
      labelKey: 'testimonials.subramanyaNataraj.label',
      quoteKey: 'testimonials.subramanyaNataraj.quote',
      name: 'Subramanya Nataraj',
      title: 'Research Assistant',
      quoteColor: 'light',
      labelColor: 'dark',
    },
  ],

  resources: {
    videoQaCard: {
      heading: 'Your burning questions answered',
      tag: "master's in ai",
    },
    videos: {
      videoQA: { id: 'is9pSpMjjjg', title: 'Your Burning Questions Answered' },
      fullWebinar: { id: 'wm6NzhGijpI', title: "Master's in AI: Everything You Need to Know" },
    },
  },

  faqs: [
    {
      question: 'What is the Udacity Institute of AI & Technology?',
      answer:
        "The Udacity Institute of AI & Technology is a member college of Woolf that provides learners with a path to a fully online master's program. Woolf is an accredited higher education institution recognized in 60+ countries. The degree consists of 2,250 credit hours, or the equivalent of about 12 Udacity Nanodegree programs and capstone project. There are 7 required Nanodegree programs and students can earn the remainder of their credits through electives. This provides a structured pathway combining project-based, industry-aligned coursework with academic recognition.",
    },
    {
      question: 'Who can apply to this degree program?',
      answer:
        "Anyone with an undergraduate degree is eligible to apply. If you don't have a degree, you may still be eligible through the Performance-Based Admission (PBA) pathway.",
    },
    {
      question: 'Can I apply the hours from past Udacity programs toward this degree?',
      answer:
        "Yes! If you've previously completed eligible Udacity coursework, your study hours may already count toward the degree. You'll just need to enroll in the program to convert those hours into official academic credit.",
    },
    {
      question: 'Are the credits I earn from this program transferrable to other colleges or universities?',
      answer:
        'Yes! Woolf is an accredited higher education institution recognized in 60+ countries.You can typically transfer credits to other institutions depending on their acceptance policies.',
    },
    {
      question: 'Am I required to pay the full program fee of $5000 USD?',
      answer:
        'No. Getting started simply requires you to pay the enrollment fee of $199 USD and be a Udacity subscriber which starts at $249 USD a month. The estimated cost of $5000 assumes it takes 19-months to finish the full program at $249/month. However, you can cancel at anytime.',
    },
    {
      question: 'Do I get credit for the work I do if I cancel my subscription?',
      answer:
        "As you successfully complete each course in the Master's degree program, you earn corresponding academic credit. If you pause or cancel, you will still have an academic transcript with ECTS credits for your coursework that you can transfer to another program.",
    },
    {
      question: "Do I need to repeat a Udacity Nanodegree program for the Master's degree if I've previously taken it?",
      answer:
        "No. If you've previously graduated from a Nanodegree program that's included in the Master's degree, you will receive credit for that Nanodegree program and will not need to take it again. However, you will be required to complete the remediation exercise for that Nanodegree program which includes required academic reading and an assessment to ensure you understand the material.",
    },
    {
      question: 'Where is the Woolf degree from the Udacity Institute of AI and Technology recognized?',
      answer:
        'A Woolf degree is equivalent to a degree from any accredited European university, as Woolf uses the ECTS system. Within the European Higher Education Area, Woolf degrees are recognized under the Lisbon Recognition Convention, upheld by ~57 countries. Additionally, Woolf degrees are considered equivalent to those from accredited universities in the US, Canada, and more. For validation checks (e.g., for immigration), students can email support@woolf.education.',
    },
    {
      question: 'How long does it take to complete the degree?',
      answer:
        'The program is self-paced, so the time it takes depends on your schedule and learning pace. A learner who spends 25 hours a week on the program will complete it in just under 24 months. Most learners typically complete the degree in 18\u201324 months.',
    },
    {
      question:
        "How long does it take for my previous learning in Udacity to apply as credits towards the Master's program?",
      answer:
        "After you apply it can take up to five business days for the work you've previously completed in Udactiy to be added to your transcript as academic credit.",
    },
    {
      question: 'Can I take a break and pause the program?',
      answer:
        "Yes. You can pause your subscription for up to 90 days at a time. During the pause, you remain enrolled in the degree program, and your progress is retained. Once you reactivate your subscription and resume payment, you'll be able to continue your learning. After resuming, you'll need to stay active for at least one month before pausing again.",
    },
    {
      question: 'Do I need to stay subscribed to Udacity to remain in the program?',
      answer:
        "Yes. As long as your Udacity subscription is active, you'll stay enrolled in the master's program. If you cancel your subscription or request to unenroll, your participation ends. However, if you re-enroll later, your degree progress will be retained.",
    },
    {
      question: 'What does the $199 enrollment fee cover?',
      answer:
        'The fee covers enrollment processing, admission review, and academic credit conversion. It supports the systems that issue and manage your official academic credit.',
    },
    {
      question: 'Do I have to pay the fee again if I re-enroll later?',
      answer:
        'No. If you cancel your subscription and later decide to re-enroll, you are not required to pay the fee again. You can contact Udacity Support to reactivate your degree program and continue from where you left off.',
    },
    {
      question: 'What is Woolf?',
      answer:
        "Woolf is a global collegiate higher education institution that lets qualified organizations join as new member colleges and offer accredited degrees. Woolf's mission is to increase access to world-class higher education and ensure that it is globally recognized and transferable. Woolf is composed of 29 member colleges, and degrees from Woolf are fully accredited in the ECTS system and recognized in 60+ countries.",
    },
    {
      question: "Can I enroll in two master's degree programs simultaneously?",
      answer:
        'Yes, you can be enrolled in two master\u2019s degree programs at the same time. You would have to separately pay the one-time enrollment fee and complete the application for each degree program. Having a Udacity subscription is a prerequisite for enrollment.',
    },
  ],
  faqsSupportLink: 'https://support.udacity.com/hc/en-us/categories/40277260643725-Udacity-Institute-of-AI-Technology',

  footer: {
    heading: 'Ready to start your journey?',
    description:
      "One short application and a one-time $199 enrollment fee. That's all it takes to start working towards your Master of Science in Artificial Intelligence.",
    backgroundImages: {
      mobile: '/images/accreditation/footer-bg-mobile.png',
      desktop: '/images/accreditation/footer-bg.png',
    },
  },

  newsItems: mastersAiNewsItems,
};
